import clsx from 'clsx';
import { useAtomValue, useSetAtom } from 'jotai';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { FLOATING_CHAT_DOCK_COLUMN_ATTRIBUTE, useStoredFloatingChatGeometry } from '@/hooks/use-floating-chat-surface';
import { docuAiDockAtom, docuAiDockColumnsAtom, type DocuAiDockColumn } from '@/state';

import styles from './docuai-dock-slot.module.scss';

/**
 * Space reserved at the bottom of a right sidebar for the DocuAI launcher, which overlays it as a fixed
 * surface. Shows the dashed drop placeholder while the launcher is dragged over the column.
 */
export const DocuAiDockSlot = ({ column, className }: DocuAiDockSlotProps) => {
  const dock = useAtomValue(docuAiDockAtom);
  const [{ docked: preferredDocked }] = useStoredFloatingChatGeometry();

  // Until a chat claims the dock (it mounts after the sidebar), reserve from the stored preference so the
  // first paint already has the space and the claim changes nothing. Whether the chat is open makes no
  // difference — a docked chat keeps its slot reserved while open, so the claim never moves content.
  const ownsDock = dock.column === column;
  const unclaimed = dock.column === null;
  const state = ownsDock ? dock.state : unclaimed && preferredDocked ? 'docked' : 'hidden';

  // The height transition is enabled a frame after this column owns the dock and disabled when it is
  // released, so neither a claim nor a later re-claim animates.
  const [animateHeight, setAnimateHeight] = useState(false);
  useEffect(() => {
    if (!ownsDock) {
      setAnimateHeight(false);
      return;
    }
    const frame = requestAnimationFrame(() => setAnimateHeight(true));
    return () => cancelAnimationFrame(frame);
  }, [ownsDock]);

  // Registers the enclosing column element so the chat surface can dock into it even when the column
  // appears (or its attribute is toggled) after the surface mounted. Re-checked every render since the
  // attribute can change without this slot's props changing.
  const setColumns = useSetAtom(docuAiDockColumnsAtom);
  const slotRef = useRef<HTMLDivElement>(null);
  const registered = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const element = slotRef.current?.closest<HTMLElement>(`[${FLOATING_CHAT_DOCK_COLUMN_ATTRIBUTE}="${column}"]`) ?? null;
    if (element === registered.current) return;
    registered.current = element;
    setColumns((columns) => ({ ...columns, [column]: element }));
  });
  useLayoutEffect(
    () => () => {
      const element = registered.current;
      if (!element) return;
      registered.current = null;
      // Only clear our own registration: a sibling slot for the same column may already have replaced it.
      setColumns((columns) => (columns[column] === element ? { ...columns, [column]: null } : columns));
    },
    [column, setColumns]
  );

  return (
    <div
      ref={slotRef}
      aria-hidden
      data-testid="docuai-dock-slot"
      data-state={state}
      data-animate={animateHeight || undefined}
      className={clsx(styles.slot, className)}
    >
      <div className={styles.placeholder}>
        <span className={styles.placeholderLabel}>Drop here to mount</span>
      </div>
    </div>
  );
};

interface DocuAiDockSlotProps {
  column: DocuAiDockColumn;
  className?: string;
}
